// Feedback layer. Idle games live or die on the half-second after a tap: the
// number that leaps off the button, the ring that snaps outward, the frame that
// kicks. All of it is throwaway DOM in a fixed overlay mounted outside #app, so
// the command view's redraw never interrupts an animation in flight.

let layer = null;
let shakeTimer = 0;

function ensureLayer() {
  if (layer?.isConnected) return layer;
  layer = document.createElement('div');
  layer.className = 'fx-layer';
  layer.setAttribute('aria-hidden', 'true');
  document.body.appendChild(layer);
  return layer;
}

function spawn(node, lifetime) {
  ensureLayer().appendChild(node);
  setTimeout(() => node.remove(), lifetime);
  return node;
}

// Where should a burst originate? Callers usually have an element, not a point.
export function centerOf(element) {
  if (!element) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

// Rising, fading text — the "+1,240" that makes a purchase feel paid for.
export function floatText(x, y, text, { tone = 'cyan', size = 1, drift = 0 } = {}) {
  const node = document.createElement('span');
  node.className = `fx-float fx-${tone}`;
  node.textContent = text;
  node.style.left = `${x}px`;
  node.style.top = `${y}px`;
  node.style.setProperty('--fx-size', size);
  node.style.setProperty('--fx-drift', `${drift}px`);
  return spawn(node, 1400);
}

// A scatter of sparks. `spread` is the radius they fly out to.
export function burst(x, y, { count = 10, tone = 'cyan', spread = 70 } = {}) {
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2 + Math.random() * 0.6;
    const distance = spread * (0.45 + Math.random() * 0.75);
    const spark = document.createElement('i');
    spark.className = `fx-spark fx-${tone}`;
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    spark.style.setProperty('--fx-dx', `${Math.cos(angle) * distance}px`);
    spark.style.setProperty('--fx-dy', `${Math.sin(angle) * distance}px`);
    spark.style.animationDelay = `${Math.random() * 60}ms`;
    spawn(spark, 900);
  }
}

// Expanding shockwave ring. Reads as impact without obscuring anything.
export function ring(x, y, { tone = 'cyan', size = 120 } = {}) {
  const node = document.createElement('i');
  node.className = `fx-ring fx-${tone}`;
  node.style.left = `${x}px`;
  node.style.top = `${y}px`;
  node.style.setProperty('--fx-ring', `${size}px`);
  return spawn(node, 800);
}

// Full-bleed colour wash for rank-ups, mode results, catastrophes.
export function flash(tone = 'cyan', strength = 0.35) {
  const node = document.createElement('div');
  node.className = `fx-flash fx-${tone}`;
  node.style.setProperty('--fx-strength', strength);
  return spawn(node, 700);
}

// Camera kick. Applied to <body> so overlays and the canvas move together.
export function shake(strength = 6, duration = 260) {
  document.body.style.setProperty('--fx-shake', `${strength}px`);
  document.body.classList.add('fx-shaking');
  clearTimeout(shakeTimer);
  shakeTimer = setTimeout(() => document.body.classList.remove('fx-shaking'), duration);
}

// Big centred announcement — mode start, wave clear, promotion.
export function banner(title, subtitle = '', tone = 'cyan') {
  const node = document.createElement('div');
  node.className = `fx-banner fx-${tone}`;
  // Two banners can land on the same frame — a wave clearing at the same moment
  // a personal best is set. Stack them instead of printing one over the other.
  const live = ensureLayer().querySelectorAll('.fx-banner').length;
  node.style.setProperty('--fx-stack', `${live * 92}px`);
  node.innerHTML = `<strong></strong><small></small>`;
  node.querySelector('strong').textContent = title;
  node.querySelector('small').textContent = subtitle;
  return spawn(node, 2200);
}

// Convenience: everything you want when a reward lands on a specific element.
export function reward(element, text, tone = 'amber') {
  const { x, y } = centerOf(element);
  floatText(x, y - 12, text, { tone, size: 1.15 });
  burst(x, y, { tone, count: 12 });
  ring(x, y, { tone, size: 110 });
}
